import { type FC, type ReactNode, useEffect } from "react";

import { Button } from "./Button";
import { Modal } from "./Modal";

interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
  isProcessing?: boolean;
}

export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onClose,
  isProcessing = false,
}) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) {
        return;
      }

      const isTextInput =
        event.target instanceof HTMLElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(event.target.tagName);

      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      } else if (event.key === "Enter" && !isProcessing && !isTextInput) {
        event.preventDefault();
        onConfirm();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose, onConfirm, isProcessing]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      widthClass="max-w-md"
      footer={
        <div className="flex w-full justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isProcessing}>
            {cancelLabel}
          </Button>
          <Button variant="danger" onClick={onConfirm} loading={isProcessing}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="text-slate-600 dark:text-slate-300">{body}</div>
    </Modal>
  );
};
